import { useContext } from 'react';
import { GlobalContext } from '@/app/providers/globalState';

/**
 * Thin wrapper around ``GlobalContext`` for the commercial-model catalog.
 *
 * Returns:
 *   - ``availableProviders``: a map keyed by provider name,
 *     ``{ [name]: { provider_type, models: [{ id, vendor }] } }``. Direct
 *     pass-through of ``GET /available-models`` (see globalState.jsx for the
 *     full doc and the backend's ``ModelInfo`` type in optune-utils'
 *     ``llm_gateway/base.py``). Callers derive vendor / cloud subsets, per-
 *     provider model lists, and bedrock catalogs from this single map.
 *   - ``isLoading``: ``true`` while the initial fetch (or a refresh) is in
 *     flight. Callers use this to avoid flashing "no providers configured"
 *     banners on first paint.
 *   - ``refresh``: re-fetches ``GET /available-models`` and rewrites the
 *     localStorage cache. Surfaces that let the user pick a model for a new
 *     run (Retrieval Agent, Generate Data with AI) call it when they open so
 *     a tab left open across a credentials change never offers a provider
 *     that has no keys any more.
 *
 * The catalog is fetched once at app load and refreshed when the user saves
 * new keys / cloud credentials in Account Settings (see ``Settings.jsx``) or
 * through ``refresh``. The catalog is global (not per-client / per-usecase)
 * so this hook intentionally takes no arguments.
 */
export function useCommercialModels() {
    const {
        availableProviders,
        isCommercialModelsLoading,
        refreshCommercialModels,
    } = useContext(GlobalContext);

    return {
        availableProviders,
        isLoading: isCommercialModelsLoading,
        refresh: refreshCommercialModels,
    };
}
